// src/screens/PublicProfileScreen.js
// Public player profile — the clickable identity behind the leaderboard.
// Hero (name/photo/bio/website/socials/badges) + overall stats + tabs
// (By Game · Recent Results · Upcoming Events). The account owner can edit
// bio/website/socials (sanitized server-side); visitors get read-only + links.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View, Text, Pressable, ScrollView, Image, ActivityIndicator,
  StyleSheet, Platform, Modal, TextInput, Linking,
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useIsFocused } from '@react-navigation/native';
import { FontAwesome6, Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import * as Clipboard from 'expo-clipboard';
import { auth } from '../../firebaseConfig';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import FounderBadge from '../components/FounderBadge';
import { setPageMeta, resetPageMeta } from '../utils/pageMeta';
import { useCritsConfirmedOn } from '../hooks/useCritsEnabled';
import JoinMembershipModal from '../components/JoinMembershipModal';
import { restoreFocus } from '../components/MemberPanel';
import { useMyChannelMembership, useMyMembership, teamDisplayName } from '../utils/membershipPayment';
import {
  tierNameOf, videoTierChip, yourTierLine, profileRowPrice, PROFILE_CARD_TITLE, JOIN_LABEL, SWITCH_TIER_LABEL, profileCanSwitch,
} from '../utils/membershipTiers';

const WORDMARK = require('../../assets/critcash_logo_transparent.webp');

// Known socials — label, brand color, and the real brand logo (FontAwesome6
// Brands). Kick has no icon in FA6, so it keeps a text glyph fallback.
const SOCIALS = [
  { key: 'twitch', label: 'Twitch', color: '#9146FF', icon: 'twitch' },
  { key: 'youtube', label: 'YouTube', color: '#FF0000', icon: 'youtube' },
  { key: 'x', label: 'X', color: '#111', icon: 'x-twitter' },
  { key: 'instagram', label: 'Instagram', color: '#E1306C', icon: 'instagram' },
  { key: 'tiktok', label: 'TikTok', color: '#000', icon: 'tiktok' },
  { key: 'kick', label: 'Kick', color: '#53FC18', glyph: 'K' },
  { key: 'discord', label: 'Discord', color: '#5865F2', icon: 'discord' },
  { key: 'facebook', label: 'Facebook', color: '#1877F2', icon: 'facebook-f' },
];

// Render a social's brand logo (FA6) or its text-glyph fallback (Kick).
function SocialGlyph({ s, size }) {
  if (s.icon) return <FontAwesome6 name={s.icon} brand size={size} color="#fff" />;
  return <Text style={styles.socGlyph}>{s.glyph}</Text>;
}

const AVATAR_COLORS = ['#FCD000', '#00bfff', '#b06cff', '#4ade80', '#ff5c8a', '#f59e0b'];
function colorFor(uid) {
  let h = 0; for (let i = 0; i < (uid || '').length; i++) h = (h * 31 + uid.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  return String(name || '?').replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || '?';
}
function openLink(url) {
  if (url && /^https?:\/\//i.test(url)) Linking.openURL(url).catch(() => {});
}
function medal(rank, pc) {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `${rank}${rank % 10 === 1 ? 'st' : rank % 10 === 2 ? 'nd' : rank % 10 === 3 ? 'rd' : 'th'}`;
}
function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - Date.parse(iso)) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
function whenLabel(iso) {
  if (!iso) return { d: 'TBD', t: '' };
  const dt = new Date(iso);
  const day = dt.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  const t = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const isToday = new Date().toDateString() === dt.toDateString();
  return { d: isToday ? 'TODAY' : day, t };
}

export default function PublicProfileScreen({ route, navigation }) {
  const uid = route?.params?.uid || null;
  const userName = route?.params?.userName || null;
  const isFocused = useIsFocused();
  // settings/economy.critsEnabled, PUBLIC variant (fails closed): this page is
  // open to signed-out visitors, so Crit stats show only once Crits are
  // confirmed on. Off: no Crit numbers anywhere; a +N beside a placement would
  // read as a payout for the result.
  const critsEnabled = useCritsConfirmedOn();
  const [data, setData] = useState(null);
  // Follow from the profile page (evergreen Step 1.2). getPublicProfile
  // returns `following` for the signed-in viewer; toggleStreamFollow flips it.
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  useEffect(() => { setFollowing(!!data?.following); }, [data?.following]);
  const toggleFollow = async () => {
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    if (!data?.uid || followBusy) return;
    try {
      setFollowBusy(true);
      const fn = httpsCallable(getFunctions(), 'toggleStreamFollow');
      const { data: res } = await fn({ userId: data.uid });
      setFollowing(!!res?.following);
    } catch (_) { /* leave state as is */ } finally { setFollowBusy(false); }
  };
  const [loading, setLoading] = useState(true);
  // Land on Videos first — a channel's content leads, not the (usually empty at
  // launch) match stats (founder 2026-07-25).
  const [tab, setTab] = useState('videos');
  const [editing, setEditing] = useState(false);
  const [copiedSoc, setCopiedSoc] = useState(null); // social key whose value was just copied
  // Videos tab (lazy-loaded on first open)
  const [vids, setVids] = useState(null);
  const [vidsLoading, setVidsLoading] = useState(false);
  const [vidGame, setVidGame] = useState(null);   // filter: game
  const [vidSort, setVidSort] = useState('new');  // 'new' | 'likes' | 'views'

  const isOwner = !!data && auth.currentUser && auth.currentUser.uid === data.uid;

  // Channel memberships (3.5a). The viewer's own pair doc, listened to
  // directly; a falsy uid, a signed-out visitor or the owner get the hook's
  // constant object (no listener). Join shows only on the server's
  // membershipsEnabled and a settled "not a member"; Member shows even after
  // the channel stops selling. While the hook loads or errs, neither.
  const m = useMyChannelMembership(data?.uid || null);
  const showMember = !!data && !isOwner && m.isMember;
  const showJoin = !!data && !isOwner && data.membershipsEnabled === true && !m.loading && !m.error && !m.isMember;
  // The Join modal's target {kind, id, name} (evergreen 3.5b: this page's
  // channel, or the team this streamer is on). Keyed join:<kind>:<id> like
  // the Rumble picker, so a target change unmounts it with its form.
  const [joinOpenFor, setJoinOpenFor] = useState(null);
  // Tiers (R5): 'switch' opens the modal at its switch stage (a member's
  // "Switch tier"); null = the join panel.
  const [joinOpenTo, setJoinOpenTo] = useState(null);
  const openJoin = (to = null) => {
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    setJoinOpenTo(to === 'switch' ? 'switch' : null);
    if (data?.uid) setJoinOpenFor({ kind: 'channel', id: data.uid, name: data.profile?.userName });
  };
  // Team upsell (3.5b, 13.10): "Also streams with Team X. Join Team X."
  // from getPublicProfile's team field and the viewer's own team pair doc
  // (the constant object with no team or signed out). The server hint is
  // already false for the owner and anyone on the roster. Money-free.
  const upsellTeam = data?.team && typeof data.team === 'object' && data.team.teamId ? data.team : null;
  const tm = useMyMembership('team', upsellTeam?.teamId || null);
  const showTeamUpsell = !!upsellTeam && upsellTeam.membershipsEnabled === true
    && !tm.loading && !tm.error && !tm.isMember;
  const showTeamMemberRow = !!upsellTeam && tm.isMember === true;
  const upsellTeamNoun = teamDisplayName(upsellTeam?.name);
  const teamUpsellRef = useRef(null);
  const joinOpenerRef = useRef(null);
  const openTeamJoin = () => {
    if (!showTeamUpsell || !upsellTeam) return;
    if (!auth.currentUser) { navigation.navigate('SignUp'); return; }
    joinOpenerRef.current = teamUpsellRef.current;
    setJoinOpenTo(null);
    setJoinOpenFor({ kind: 'team', id: upsellTeam.teamId, name: upsellTeam.name });
  };
  const closeJoin = () => {
    setJoinOpenFor(null);
    setJoinOpenTo(null);
    restoreFocus(joinOpenerRef);
    joinOpenerRef.current = null;
  };
  // The Memberships card (F8): the tiers that are on, from the server.
  const profileTiers = (Array.isArray(data?.membershipTiers) ? data.membershipTiers : [])
    .filter((t) => t && tierNameOf(t.tierKey) && Number(t.priceCents) > 0);
  // Joining here clears the videos tab's lock chips without a reload: on the
  // hook's isMember false -> true edge, drop the list so the lazy loader
  // fetches it again (on the Videos tab now, or when it next opens).
  const wasMember = useRef(m.isMember);
  useEffect(() => {
    const was = wasMember.current;
    wasMember.current = m.isMember;
    if (!was && m.isMember) setVids((cur) => (cur === null ? cur : null));
  }, [m.isMember]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fn = httpsCallable(getFunctions(), 'getPublicProfile');
      const { data: res } = await fn({ uid, userName });
      setData(res);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [uid, userName]);

  useEffect(() => { load(); }, [load]);

  // Sender-side share previews read the LIVE DOM (see utils/pageMeta) —
  // mirror serveSocialPreview's /u/ card while this profile is focused,
  // restore the default card on blur/unmount (screens can stay mounted
  // under the stack, so unmount alone isn't enough).
  useEffect(() => {
    if (!isFocused) { resetPageMeta(); return; }
    const prof = data?.profile;
    if (prof?.userName) {
      setPageMeta({
        title: `${prof.userName} on CritCash`,
        description: prof.bio ||
          `Check out ${prof.userName}'s profile, events, and streams on CritCash.`,
        url: `https://www.critcash.com/u/${encodeURIComponent(prof.userName)}`,
        image: prof.bannerUrl || undefined,
      });
    }
    return () => resetPageMeta();
  }, [isFocused, data]);

  // Lazy-load published videos the first time the Videos tab opens.
  useEffect(() => {
    if (tab !== 'videos' || vids !== null || !data?.uid) return;
    let alive = true;
    setVidsLoading(true);
    httpsCallable(getFunctions(), 'getUserPublicVideos')({ uid: data.uid })
      .then((res) => { if (alive) setVids(res.data?.videos || []); })
      .catch(() => { if (alive) setVids([]); })
      .finally(() => { if (alive) setVidsLoading(false); });
    return () => { alive = false; };
  }, [tab, vids, data?.uid]);

  if (loading) {
    return (
      <View style={styles.screen}>
        <Header navigation={navigation} />
        <View style={styles.center}><ActivityIndicator size="large" color="#FCD000" /></View>
      </View>
    );
  }
  if (!data) {
    return (
      <View style={styles.screen}>
        <Header navigation={navigation} />
        <View style={styles.center}>
          <Text style={styles.emptyTxt}>Profile not found.</Text>
          <Pressable style={styles.backBtn} onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Leaderboard'))}><Text style={styles.backLabel}>BACK</Text></Pressable>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </View>
    );
  }

  const p = data.profile || {};
  const stats = data.stats || {};
  const activeSocials = SOCIALS.filter((s) => p.socials && p.socials[s.key]);

  return (
    <View style={styles.screen}>
      <Header navigation={navigation} />
      <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        <View style={styles.card}>
          {/* Channel banner (Twitch/YouTube model) — always renders: the
              uploaded art, or a branded default with an owner add-prompt. */}
          <View style={styles.bannerWrap}>
            {p.bannerUrl ? (
              <Image source={{ uri: p.bannerUrl }} style={styles.banner} resizeMode="cover" />
            ) : (
              <View style={styles.bannerDefault}>
                <Image source={WORDMARK} style={styles.bannerMark} resizeMode="contain" />
                {isOwner && (
                  <Pressable style={styles.bannerAdd} onPress={() => setEditing(true)}>
                    <Text style={styles.bannerAddTxt}>＋ Add a banner</Text>
                  </Pressable>
                )}
              </View>
            )}
            <Pressable
              onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Leaderboard'))}
              style={[styles.backBtn, styles.backOverBanner]}
            >
              <Text style={styles.backLabel}>BACK</Text>
            </Pressable>
          </View>

          {/* Channel header */}
          <View style={styles.hero}>
            <View style={styles.idRow}>
              <View style={styles.avatarLift}>
                <FounderBadge size={88} active={data.profileBadge === 'founder'}>
                  {p.photoUrl ? (
                    <Image source={{ uri: p.photoUrl }} style={[styles.avatar, styles.avatarXl]} />
                  ) : (
                    <View style={[styles.avatar, styles.avatarXl, styles.avatarInit, { backgroundColor: colorFor(data.uid) }]}>
                      <Text style={styles.avatarTxt}>{initials(p.userName)}</Text>
                    </View>
                  )}
                </FounderBadge>
              </View>
              <View style={{ flex: 1, minWidth: 0, paddingTop: 6 }}>
                <Text style={styles.name} numberOfLines={1}>{p.userName}</Text>
                <Text style={styles.meta} numberOfLines={1}>
                  {[p.joinedAt ? `Joined ${new Date(p.joinedAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}` : '', p.country || ''].filter(Boolean).join(' · ')}
                </Text>
                <View style={styles.badges}>
                  {data.fullCrown && (
                    <Text style={[styles.badge, styles.badgeGold]}>
                      👑 Full Crown · {data.verifiedSellouts} sellout{data.verifiedSellouts === 1 ? '' : 's'}
                    </Text>
                  )}
                  {data.profileBadge === 'founder' && (
                    <Text style={[styles.badge, styles.badgeFounder]}>
                      FOUNDER{data.founderNumber ? ` #${data.founderNumber}` : ''}
                    </Text>
                  )}
                  {data.topGame && data.topGame.wins > 0 && (
                    <Text style={[styles.badge, styles.badgeGold]}>★ {data.topGame.name} · {data.topGame.wins}W</Text>
                  )}
                  {(stats.events || 0) > 0 && (
                    <Text style={[styles.badge, styles.badgeCyan]}>{stats.events} events played</Text>
                  )}
                </View>
              </View>
              <View style={styles.headerAction}>
                {isOwner ? (
                  <Pressable style={[styles.btn, styles.btnGold]} onPress={() => setEditing(true)}>
                    <Text style={styles.btnGoldTxt}>Customize channel</Text>
                  </Pressable>
                ) : data?.isLive ? (
                  // Live → watch the stream. Watch URLs carry the public handle,
                  // never the uid (2026-07-23); "Player xxxxx" is the handle-less
                  // placeholder, so those fall back to the uid.
                  <Pressable style={[styles.btn, styles.btnGold]} onPress={() => navigation.navigate('StreamWatch', { channel: (p.userName && !p.userName.startsWith('Player ')) ? p.userName : data.uid })}>
                    <Text style={styles.btnGoldTxt}>Watch Live Now</Text>
                  </Pressable>
                ) : (
                  // Offline → disabled, non-navigating (founder 2026-07-25): the
                  // button state removes the "is there anything to watch?" ambiguity.
                  <View style={[styles.btn, styles.btnOffline]}>
                    <Text style={styles.btnOfflineTxt}>Offline</Text>
                  </View>
                )}
                {!isOwner ? (
                  <Pressable style={[styles.btn, following ? styles.btnFollowing : styles.btnFollow, followBusy && { opacity: 0.6 }]} onPress={toggleFollow} disabled={followBusy}>
                    <Text style={following ? styles.btnFollowingTxt : styles.btnFollowTxt}>{following ? 'Following' : 'Follow'}</Text>
                  </Pressable>
                ) : null}
                {showMember ? (
                  <Pressable style={[styles.btn, styles.btnMember]} onPress={openJoin} accessibilityRole="button" accessibilityLabel="Member">
                    <Ionicons name="star" size={12} color="#FCD000" style={{ marginRight: 5 }} />
                    <Text style={styles.btnMemberTxt}>Member</Text>
                  </Pressable>
                ) : showJoin ? (
                  <Pressable style={[styles.btn, styles.btnJoin]} onPress={openJoin} accessibilityRole="button" accessibilityLabel="Join">
                    <Ionicons name="star-outline" size={12} color="#FCD000" style={{ marginRight: 5 }} />
                    <Text style={styles.btnJoinTxt}>Join</Text>
                  </Pressable>
                ) : null}
                {!data?.isLive && data?.nextStreamAtMs ? (
                  <Text style={styles.nextStreamTxt}>Next stream {new Date(data.nextStreamAtMs).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}</Text>
                ) : null}
                {/* Team upsell (3.5b): ONE Pressable whose whole text is its
                    label; a member of the team gets a quieter link instead. */}
                {showTeamUpsell ? (
                  <Pressable
                    ref={teamUpsellRef}
                    style={[styles.btn, styles.btnJoin, styles.teamUpsell]}
                    onPress={openTeamJoin}
                    accessibilityRole="button"
                    accessibilityLabel={`Also streams with ${upsellTeamNoun}. Join ${upsellTeamNoun}.`}
                  >
                    <Ionicons name="people-outline" size={13} color="#FCD000" style={{ marginRight: 6 }} />
                    <Text style={[styles.btnJoinTxt, styles.teamUpsellTxt]}>
                      {`Also streams with ${upsellTeamNoun}. Join ${upsellTeamNoun}.`}
                    </Text>
                  </Pressable>
                ) : showTeamMemberRow ? (
                  <Pressable
                    style={styles.teamMemberRow}
                    onPress={() => navigation.navigate('TeamPage', { teamId: upsellTeam.teamId })}
                    accessibilityRole="link"
                    accessibilityLabel={`Also streams with ${upsellTeamNoun}.`}
                  >
                    <Ionicons name="people-outline" size={13} color="#8a8a95" style={{ marginRight: 6 }} />
                    <Text style={styles.teamMemberRowTxt}>{`Also streams with ${upsellTeamNoun}.`}</Text>
                  </Pressable>
                ) : null}
              </View>
            </View>

            {!!p.bio && <Text style={styles.bio}>{p.bio}</Text>}
            {!!p.website && (
              <Pressable onPress={() => openLink(p.website)}>
                <Text style={styles.web} numberOfLines={1}>🔗 {p.website.replace(/^https?:\/\//, '')}</Text>
              </Pressable>
            )}

            {(activeSocials.length > 0 || isOwner) && (
              <View style={styles.socials}>
                {activeSocials.map((s) => {
                  const val = p.socials[s.key];
                  const isLink = /^https?:\/\//i.test(val);
                  return (
                    <Pressable
                      key={s.key}
                      onPress={async () => {
                        if (isLink) return openLink(val);
                        // Non-link socials (Discord usernames) copy instead.
                        try { await Clipboard.setStringAsync(val); } catch {}
                        setCopiedSoc(s.key);
                        setTimeout(() => setCopiedSoc(null), 1600);
                      }}
                      style={[styles.soc, !isLink && styles.socWide, { backgroundColor: s.color }]}
                    >
                      <SocialGlyph s={s} size={17} />
                      {!isLink && (
                        <Text style={styles.socCopyTxt} numberOfLines={1}>
                          {copiedSoc === s.key ? 'Copied!' : val}
                        </Text>
                      )}
                    </Pressable>
                  );
                })}
                {isOwner && (
                  <Pressable onPress={() => setEditing(true)} style={styles.socAdd}>
                    <Text style={styles.socAddTxt}>＋</Text>
                  </Pressable>
                )}
              </View>
            )}

          </View>

          {/* Memberships card (tiers, R5, 5.11 F8): the tiers the creator has
              on (getPublicProfile's membershipTiers, sellable only), each
              with its price; Join for a viewer, the member's own tier and
              "Switch tier" for a member. */}
          {!isOwner && profileTiers.length && (showJoin || showMember) ? (
            <View style={styles.tierCard}>
              <Text style={styles.tierCardTitle}>{PROFILE_CARD_TITLE}</Text>
              {profileTiers.map((t) => (
                <View key={t.tierKey} style={styles.tierCardRow}>
                  <Text style={styles.tierCardName}>{tierNameOf(t.tierKey)}</Text>
                  <Text style={styles.tierCardPrice}>{profileRowPrice(t.priceCents)}</Text>
                </View>
              ))}
              {showMember && tierNameOf(m.doc?.tierKey) ? (
                <View style={styles.tierCardMember}>
                  <Text style={styles.tierCardMemberTxt}>{yourTierLine(tierNameOf(m.doc.tierKey))}</Text>
                  {profileCanSwitch(m.doc, profileTiers) ? (
                    <Pressable style={[styles.btn, styles.btnJoin]} onPress={() => openJoin('switch')} accessibilityRole="button">
                      <Text style={styles.btnJoinTxt}>{SWITCH_TIER_LABEL}</Text>
                    </Pressable>
                  ) : null}
                </View>
              ) : showJoin ? (
                <Pressable style={[styles.btn, styles.btnJoin, styles.tierCardJoin]} onPress={() => openJoin()} accessibilityRole="button">
                  <Ionicons name="star-outline" size={12} color="#FCD000" style={{ marginRight: 5 }} />
                  <Text style={styles.btnJoinTxt}>{JOIN_LABEL}</Text>
                </Pressable>
              ) : null}
            </View>
          ) : null}

          {/* Stats */}
          <View style={styles.stats}>
            {[...(critsEnabled ? [['Crits Earned', stats.crits, '#FCD000']] : []), ['Wins', stats.wins, '#4ade80'], ['Podiums', stats.podiums, '#fff'], ['Events', stats.events, '#fff']].map(([l, v, c]) => (
              <View key={l} style={styles.stat}>
                <Text style={[styles.statV, { color: c }]}>{(Number(v) || 0).toLocaleString()}</Text>
                <Text style={styles.statL}>{l}</Text>
              </View>
            ))}
          </View>

          {/* Tabs */}
          <View style={styles.tabs}>
            {[['videos', 'Videos'], ['game', 'By Game'], ['results', 'Recent Results'], ['up', 'Upcoming Events']].map(([k, l]) => (
              <Pressable key={k} onPress={() => setTab(k)}><Text style={[styles.tab, tab === k && styles.tabOn]}>{l}</Text></Pressable>
            ))}
          </View>

          <View style={styles.body}>
            {tab === 'game' && (
              (data.byGame || []).length === 0
                ? <Text style={styles.bodyEmpty}>No games played yet.</Text>
                : (
                  <>
                    <View style={[styles.gRow, styles.gHead]}>
                      <Text style={styles.gName}>GAME</Text>
                      <Text style={styles.gNum}>EVENTS</Text><Text style={styles.gNum}>W–L</Text><Text style={styles.gNum}>POD</Text>{critsEnabled && <Text style={styles.gNum}>CRITS</Text>}
                    </View>
                    {data.byGame.map((g) => (
                      <View key={g.id} style={styles.gRow}>
                        <Text style={styles.gName} numberOfLines={1}>{g.name}</Text>
                        <Text style={styles.gNum}>{g.events}</Text>
                        <Text style={styles.gNum}><Text style={styles.win}>{g.wins}</Text>–<Text style={styles.loss}>{g.losses}</Text></Text>
                        <Text style={styles.gNum}>{g.podiums}</Text>
                        {critsEnabled && <Text style={[styles.gNum, styles.crit]}>{(g.crits || 0).toLocaleString()}</Text>}
                      </View>
                    ))}
                  </>
                )
            )}

            {tab === 'results' && (
              (data.recent || []).length === 0
                ? <Text style={styles.bodyEmpty}>No results yet.</Text>
                : data.recent.map((r, i) => (
                  <View key={i} style={styles.res}>
                    <View style={[styles.place, r.rank === 1 && styles.p1, r.rank === 2 && styles.p2, r.rank === 3 && styles.p3]}>
                      <Text style={styles.placeTxt}>{medal(r.rank, r.participantCount)}</Text>
                    </View>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={styles.resName} numberOfLines={1}>{r.eventName || 'Event'}</Text>
                      <Text style={styles.resSub} numberOfLines={1}>
                        {r.game ? `${r.game.toUpperCase()} · ` : ''}{r.format ? `${r.format} · ` : ''}{r.rank} of {r.participantCount || '—'}
                      </Text>
                    </View>
                    <View>
                      {critsEnabled && <Text style={styles.resCrit}>+{(r.crits || 0).toLocaleString()}</Text>}
                      <Text style={styles.resTime}>{timeAgo(r.at)}</Text>
                    </View>
                  </View>
                ))
            )}

            {tab === 'up' && (
              (data.upcoming || []).length === 0
                ? <Text style={styles.bodyEmpty}>No upcoming events entered.</Text>
                : data.upcoming.map((u, i) => {
                  const w = whenLabel(u.startsAt);
                  return (
                    <Pressable key={i} style={styles.res} onPress={() => navigation.navigate('EventWatch', { eventId: u.eventId })}>
                      <View style={styles.when}>
                        <Text style={styles.whenD}>{w.d}</Text>
                        <Text style={styles.whenT}>{w.t}</Text>
                      </View>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={styles.resName} numberOfLines={1}>{u.name}</Text>
                        <Text style={styles.resSub} numberOfLines={1}>
                          {u.gameName ? `${u.gameName} · ` : ''}{u.format ? `${u.format} · ` : ''}{u.enteredCount} entered
                        </Text>
                      </View>
                      {critsEnabled && u.entryCritCost > 0 && <Text style={styles.inPill}>{u.entryCritCost} Crits</Text>}
                    </Pressable>
                  );
                })
            )}

            {tab === 'videos' && (() => {
              if (vidsLoading || vids === null) {
                return <View style={{ paddingVertical: 24, alignItems: 'center' }}><ActivityIndicator color="#FCD000" /></View>;
              }
              const games = [...new Set(vids.map((v) => v.game).filter(Boolean))];
              let shown = vidGame ? vids.filter((v) => v.game === vidGame) : [...vids];
              if (vidSort === 'likes') shown.sort((a, b) => (b.likes || 0) - (a.likes || 0));
              else if (vidSort === 'views') shown.sort((a, b) => (b.views || 0) - (a.views || 0));
              else shown.sort((a, b) => (b.sessionStartMs || 0) - (a.sessionStartMs || 0));
              const fmtDur = (secs) => {
                const s = Math.max(0, Math.floor(secs || 0));
                const m = Math.floor(s / 60);
                return m >= 60 ? `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}` : `${m}:${String(s % 60).padStart(2, '0')}`;
              };
              return (
                <>
                  {isOwner && (
                    <Pressable style={styles.vManageBtn} onPress={() => navigation.navigate('MyVideos')}>
                      <Text style={styles.vManageBtnTxt}>Manage My Videos ›</Text>
                    </Pressable>
                  )}
                  {vids.length === 0 ? (
                    <Text style={styles.bodyEmpty}>No videos published yet.</Text>
                  ) : (
                    <>
                      {/* Filters: game · recency · likes · views */}
                      <View style={styles.vFilters}>
                        {games.length > 0 && (
                          <>
                            <Pressable onPress={() => setVidGame(null)}>
                              <Text style={[styles.vChip, !vidGame && styles.vChipOn]}>All games</Text>
                            </Pressable>
                            {games.map((g) => (
                              <Pressable key={g} onPress={() => setVidGame(vidGame === g ? null : g)}>
                                <Text style={[styles.vChip, vidGame === g && styles.vChipOn]}>{g}</Text>
                              </Pressable>
                            ))}
                            <View style={styles.vFilterDivider} />
                          </>
                        )}
                        {[['new', 'Newest'], ['likes', 'Most liked'], ['views', 'Most viewed']].map(([k, l]) => (
                          <Pressable key={k} onPress={() => setVidSort(k)}>
                            <Text style={[styles.vChip, vidSort === k && styles.vChipOn]}>{l}</Text>
                          </Pressable>
                        ))}
                      </View>
                      <View style={styles.vGrid}>
                        {shown.map((v) => (
                          <Pressable
                            key={v.id}
                            style={styles.vCard}
                            onPress={() => navigation.navigate('VideoWatch', { videoId: v.id })}
                          >
                            <View style={styles.vThumb}>
                              {v.thumbnailUrl ? (
                                <Image source={{ uri: v.thumbnailUrl }} style={styles.vThumbImg} resizeMode="cover" />
                              ) : null}
                              <View style={styles.vPlay}><Text style={styles.vPlayTxt}>▶</Text></View>
                              {!!v.game && <Text style={styles.vGameTag}>{v.game}</Text>}
                              {/* Members-only: a lock on cards this viewer can't
                                  watch yet (the server already dropped any a Join
                                  would not unlock), a plain chip otherwise. */}
                              {v.membersOnly === true ? (
                                <View style={[styles.vMembers, v.locked === true && styles.vMembersLocked]}>
                                  <Ionicons name={v.locked === true ? 'lock-closed' : 'star'} size={9} color="#FCD000" style={{ marginRight: 3 }} />
                                  <Text style={styles.vMembersTxt}>{videoTierChip(v.membersMinTier) || 'Members'}</Text>
                                </View>
                              ) : null}
                              <Text style={styles.vDur}>{fmtDur(v.durationSeconds)}</Text>
                            </View>
                            <Text style={styles.vTitle} numberOfLines={2}>{v.title}</Text>
                            <Text style={styles.vMeta} numberOfLines={1}>
                              {v.views || 0} views · {v.likes || 0} likes{v.createdAt ? ` · ${timeAgo(v.createdAt)}` : ''}
                            </Text>
                          </Pressable>
                        ))}
                      </View>
                    </>
                  )}
                </>
              );
            })()}
          </View>

          <Text style={styles.foot}>Verified results only{critsEnabled ? ' · Crits are a non-cash competitive score' : ''} · head-to-heads, showdowns &amp; brackets</Text>
        </View>
      </ScrollView>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />

      {isOwner && <EditModal visible={editing} profile={p} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} onRefresh={load} />}
      {!isOwner && data.uid ? (() => {
        // Closed, the channel form stays mounted (the 3.5a posture); a team
        // target mounts its own keyed instance.
        const target = joinOpenFor || { kind: 'channel', id: data.uid, name: p.userName };
        return (
          <JoinMembershipModal
            key={'join:' + target.kind + ':' + target.id}
            target={target}
            creatorUid={data.uid}
            creatorName={p.userName}
            visible={!!joinOpenFor}
            openTo={target.kind === 'channel' ? joinOpenTo : null}
            onClose={closeJoin}
            navigation={navigation}
          />
        );
      })() : null}
    </View>
  );
}

// ---------------- Owner edit modal ----------------
function EditModal({ visible, profile, onClose, onSaved, onRefresh }) {
  const [bio, setBio] = useState(profile.bio || '');
  const [website, setWebsite] = useState(profile.website || '');
  const [socials, setSocials] = useState({ ...(profile.socials || {}) });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null); // 'photo' | 'banner' | 'thumb'
  const [imgMsg, setImgMsg] = useState(null);
  const [socMsg, setSocMsg] = useState(null); // shown when the server couldn't use an entry

  useEffect(() => {
    if (visible) { setBio(profile.bio || ''); setWebsite(profile.website || ''); setSocials({ ...(profile.socials || {}) }); setImgMsg(null); setSocMsg(null); }
  }, [visible]);

  // Channel images: profile photo / banner / offline stream thumbnail all go
  // through opaque-path upload callables (never raw Storage URLs). maxBytes
  // mirrors each callable's server-side cap — checked here too so an
  // oversized pick fails instantly with a clear message instead of a
  // round-trip rejection.
  const IMAGE_KINDS = {
    photo:  { fn: 'uploadProfilePhoto',    aspect: [1, 1],  quality: 0.2, maxBytes: 6 * 1024 * 1024, maxLabel: '6 MB' },
    banner: { fn: 'uploadProfileBanner',   aspect: [4, 1],  quality: 0.7, maxBytes: 4 * 1024 * 1024, maxLabel: '4 MB' },
    thumb:  { fn: 'uploadStreamThumbnail', aspect: [16, 9], quality: 0.6, maxBytes: 2 * 1024 * 1024, maxLabel: '2 MB' },
  };

  const pickAndUpload = async (kind) => {
    const cfg = IMAGE_KINDS[kind];
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) { setImgMsg('Allow photo access to upload images.'); return; }
    const picked = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: cfg.aspect,
      quality: cfg.quality,
      base64: true,
    });
    if (picked.canceled) return;
    setUploading(kind);
    setImgMsg(null);
    try {
      const asset = picked.assets[0];
      let imageBase64 = asset.base64;
      if (!imageBase64) {
        const blob = await (await fetch(asset.uri)).blob();
        imageBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
      // base64 → bytes is ~3/4; reject locally before burning the round-trip.
      const approxBytes = Math.floor(imageBase64.length * 0.75);
      if (approxBytes > cfg.maxBytes) {
        setImgMsg(`That image is ${(approxBytes / (1024 * 1024)).toFixed(1)} MB — keep it under ${cfg.maxLabel}.`);
        return;
      }
      await httpsCallable(getFunctions(), cfg.fn)({ imageBase64, contentType: 'image/jpeg' });
      setImgMsg('Image updated!');
      if (onRefresh) onRefresh();
    } catch (e) {
      setImgMsg(e?.message || 'Upload failed — try a smaller image.');
    } finally {
      setUploading(null);
    }
  };

  const removeBanner = async () => {
    setUploading('banner');
    setImgMsg(null);
    try {
      await httpsCallable(getFunctions(), 'uploadProfileBanner')({ remove: true });
      setImgMsg('Banner removed.');
      if (onRefresh) onRefresh();
    } catch (e) {
      setImgMsg(e?.message || 'Could not remove banner.');
    } finally {
      setUploading(null);
    }
  };

  const save = async () => {
    setSaving(true);
    setSocMsg(null);
    try {
      const fn = httpsCallable(getFunctions(), 'updateMyPublicProfile');
      // The server sanitizes each value and returns what it actually kept —
      // compare against what was typed so nothing vanishes silently.
      const res = await fn({ bio, website, socials });
      const saved = res?.data?.socials || {};
      const dropped = SOCIALS.filter((s) => String(socials[s.key] || '').trim() && !saved[s.key]).map((s) => s.label);
      if (String(website || '').trim() && !res?.data?.website) dropped.unshift('Website');
      if (dropped.length) {
        setWebsite(res?.data?.website || '');
        setSocials({ ...saved });
        setSocMsg(`Saved — but we couldn't use: ${dropped.join(', ')}. Paste the full profile link, or just your @handle.`);
        if (onRefresh) onRefresh();
      } else {
        onSaved();
      }
    } catch (e) {
      if (typeof window !== 'undefined') window.alert('Could not save. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.mOverlay}>
        <Pressable style={styles.mScrim} onPress={onClose} />
        <View style={styles.mCard}>
          <Text style={styles.mTitle}>Edit Profile</Text>
          <ScrollView style={{ maxHeight: 420 }} showsVerticalScrollIndicator={false}>
            <Text style={styles.mLabel}>Channel Images</Text>
            {[
              ['photo', 'Profile photo', 'Square — shown beside your name · under 6 MB'],
              ['banner', 'Profile banner', 'Wide 4:1 (1600×400) — tops your profile · under 4 MB'],
              ['thumb', 'Offline stream thumbnail', '16:9 (1280×720) — shown when offline · under 2 MB'],
            ].map(([k, label, hint]) => (
              <View key={k} style={styles.mImgRow}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.mImgLabel}>{label}</Text>
                  <Text style={styles.mHint}>{hint}</Text>
                </View>
                {k === 'banner' && !!profile.bannerUrl && (
                  <Pressable style={[styles.btn, styles.btnGhost]} onPress={removeBanner} disabled={!!uploading}>
                    <Text style={styles.btnGhostTxt}>Remove</Text>
                  </Pressable>
                )}
                <Pressable style={[styles.btn, styles.btnGhost]} onPress={() => pickAndUpload(k)} disabled={!!uploading}>
                  {uploading === k
                    ? <ActivityIndicator size="small" color="#FCD000" />
                    : <Text style={styles.btnGhostTxt}>Upload</Text>}
                </Pressable>
              </View>
            ))}
            {!!imgMsg && <Text style={[styles.mHint, { color: '#FCD000' }]}>{imgMsg}</Text>}

            <Text style={styles.mLabel}>Bio</Text>
            <TextInput
              style={[styles.mInput, styles.mArea]} value={bio} onChangeText={setBio}
              placeholder="Tell people about yourself (text only)…" placeholderTextColor="#555"
              multiline maxLength={280}
            />
            <Text style={styles.mHint}>{280 - bio.length} left · plain text only — links/HTML are removed for safety</Text>

            <Text style={styles.mLabel}>Website</Text>
            <TextInput style={styles.mInput} value={website} onChangeText={setWebsite} placeholder="yoursite.gg" placeholderTextColor="#555" autoCapitalize="none" />

            <Text style={styles.mLabel}>Socials</Text>
            <Text style={styles.mHint}>Paste your profile link, or just your @handle.</Text>
            {SOCIALS.map((s) => (
              <View key={s.key} style={styles.mSocRow}>
                <View style={[styles.mSocIcon, { backgroundColor: s.color }]}><SocialGlyph s={s} size={15} /></View>
                <TextInput
                  style={[styles.mInput, { flex: 1, marginBottom: 0 }]}
                  value={socials[s.key] || ''}
                  onChangeText={(v) => setSocials((prev) => ({ ...prev, [s.key]: v }))}
                  placeholder={s.key === 'discord' ? 'Discord username or invite link' : `${s.label} link or @handle`}
                  placeholderTextColor="#555" autoCapitalize="none"
                />
              </View>
            ))}
            {!!socMsg && <Text style={[styles.mHint, { color: '#FCD000' }]}>{socMsg}</Text>}
          </ScrollView>
          <View style={styles.mActions}>
            <Pressable style={[styles.btn, styles.btnGhost]} onPress={onClose}><Text style={styles.btnGhostTxt}>Cancel</Text></Pressable>
            <Pressable style={[styles.btn, styles.btnGold]} onPress={save} disabled={saving}>
              {saving ? <ActivityIndicator size="small" color="#000" /> : <Text style={styles.btnGoldTxt}>Save</Text>}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#06070a' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, gap: 14 },
  scroll: { padding: Platform.OS === 'web' ? 24 : 12, alignItems: 'center' },
  card: { width: '100%', maxWidth: 720, backgroundColor: '#0b0b0e', borderWidth: 1, borderColor: '#1f1f25', borderRadius: 16, overflow: 'hidden' },

  bannerWrap: { position: 'relative' },
  banner: { width: '100%', aspectRatio: 4, backgroundColor: '#0d0d12' },
  bannerDefault: {
    width: '100%', height: 96, backgroundColor: '#0e0e15',
    borderBottomWidth: 1, borderBottomColor: 'rgba(252,208,0,0.22)',
    alignItems: 'center', justifyContent: 'center',
  },
  bannerMark: { width: 110, height: 64, opacity: 0.28 },
  bannerAdd: {
    position: 'absolute', right: 10, bottom: 10,
    paddingHorizontal: 11, paddingVertical: 6, borderRadius: 16,
    borderWidth: 1, borderStyle: 'dashed', borderColor: 'rgba(252,208,0,0.5)',
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  bannerAddTxt: { color: '#FCD000', fontSize: 11.5, fontWeight: '800' },
  backOverBanner: { position: 'absolute', top: 10, left: 12, backgroundColor: 'rgba(0,0,0,0.55)' },
  hero: { position: 'relative', padding: 18, paddingTop: 12, borderBottomWidth: 1, borderBottomColor: '#1c1c22', backgroundColor: '#0d0d12' },
  backBtn: { paddingHorizontal: 11, paddingVertical: 7, borderRadius: 16, backgroundColor: 'rgba(255,255,255,0.06)' },
  backLabel: { color: '#FCD000', fontSize: 11, fontWeight: '800', letterSpacing: 0.5 },
  idRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 15, flexWrap: 'wrap' },
  avatarLift: { marginTop: -44 },
  headerAction: { paddingTop: 6 },
  avatar: { width: 72, height: 72, borderRadius: 36, backgroundColor: '#222' },
  avatarXl: { width: 88, height: 88, borderRadius: 44, borderWidth: 3, borderColor: '#0b0b0e' },
  avatarInit: { alignItems: 'center', justifyContent: 'center' },
  avatarTxt: { fontSize: 25, fontWeight: '900', color: '#000' },
  name: { fontSize: 22, fontWeight: '900', color: '#fff' },
  meta: { fontSize: 12, color: '#8a8a95', marginTop: 2 },
  badges: { flexDirection: 'row', gap: 7, marginTop: 8, flexWrap: 'wrap' },
  badge: { fontSize: 11, fontWeight: '800', paddingHorizontal: 9, paddingVertical: 4, borderRadius: 20, borderWidth: 1, overflow: 'hidden' },
  badgeGold: { color: '#FCD000', borderColor: 'rgba(252,208,0,0.5)', backgroundColor: 'rgba(252,208,0,0.08)' },
  badgeCyan: { color: '#00bfff', borderColor: 'rgba(0,191,255,0.4)', backgroundColor: 'rgba(0,191,255,0.08)' },
  badgeFounder: { color: '#aff0ff', borderColor: 'rgba(0,207,255,0.55)', backgroundColor: 'rgba(0,207,255,0.10)', letterSpacing: 0.6 },
  bio: { marginTop: 14, fontSize: 13, lineHeight: 20, color: '#c9c9d2', maxWidth: 560 },
  web: { marginTop: 8, fontSize: 12.5, fontWeight: '700', color: '#00bfff' },
  socials: { flexDirection: 'row', alignItems: 'center', gap: 9, marginTop: 13, flexWrap: 'wrap' },
  soc: { width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  socWide: { width: 'auto', flexDirection: 'row', gap: 6, paddingHorizontal: 10, maxWidth: 220 },
  socCopyTxt: { fontSize: 12, fontWeight: '700', color: '#fff' },
  socGlyph: { fontSize: 13, fontWeight: '900', color: '#fff' },
  socAdd: { width: 30, height: 30, borderRadius: 8, borderWidth: 1, borderStyle: 'dashed', borderColor: '#44444e', alignItems: 'center', justifyContent: 'center' },
  socAddTxt: { fontSize: 16, color: '#8a8a95' },
  actions: { flexDirection: 'row', gap: 8, marginTop: 15 },
  btn: { paddingHorizontal: 16, paddingVertical: 9, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  btnGold: { backgroundColor: '#FCD000' },
  btnGoldTxt: { fontSize: 12, fontWeight: '800', color: '#000' },
  btnGhost: { backgroundColor: '#17171d', borderWidth: 1, borderColor: '#2a2a32' },
  btnGhostTxt: { fontSize: 12, fontWeight: '800', color: '#cfcfd6' },
  btnOffline: { backgroundColor: '#161616', borderWidth: 1, borderColor: '#2a2a2a' },
  btnFollow: { backgroundColor: '#00e5ff', marginTop: 6 },
  btnFollowTxt: { fontSize: 12, fontWeight: '800', color: '#000' },
  btnFollowing: { backgroundColor: '#161616', borderWidth: 1, borderColor: '#00e5ff', marginTop: 6 },
  btnFollowingTxt: { fontSize: 12, fontWeight: '800', color: '#00e5ff' },
  // Channel memberships: gold, never the Follow cyan.
  btnJoin: { flexDirection: 'row', backgroundColor: 'rgba(252,208,0,0.10)', borderWidth: 1, borderColor: '#FCD000', marginTop: 6 },
  btnJoinTxt: { fontSize: 12, fontWeight: '800', color: '#FCD000' },
  btnMember: { flexDirection: 'row', backgroundColor: '#161616', borderWidth: 1, borderColor: 'rgba(252,208,0,0.5)', marginTop: 6 },
  btnMemberTxt: { fontSize: 12, fontWeight: '800', color: '#FCD000' },
  // Memberships card (tiers, R5)
  tierCard: {
    marginHorizontal: 18, marginTop: 12, marginBottom: 4, padding: 14, borderRadius: 12,
    backgroundColor: '#0d0d11', borderWidth: 1, borderColor: 'rgba(252,208,0,0.35)',
  },
  tierCardTitle: { color: '#fff', fontSize: 14, fontWeight: '800', marginBottom: 6 },
  tierCardRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 5 },
  tierCardName: { flex: 1, color: '#ececf0', fontSize: 13, fontWeight: '700' },
  tierCardPrice: { color: '#FCD000', fontSize: 13, fontWeight: '700' },
  tierCardMember: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 6 },
  tierCardMemberTxt: { color: '#cfcfd6', fontSize: 12.5, fontWeight: '700' },
  tierCardJoin: { alignSelf: 'flex-start' },
  nextStreamTxt: { color: '#FCD000', fontSize: 11, fontWeight: '700', marginTop: 6 },
  // Team upsell (3.5b): the btnJoin gold, the column's full width, wrapping.
  teamUpsell: { alignSelf: 'stretch', maxWidth: 240, minHeight: 36, alignItems: 'center', justifyContent: 'flex-start', paddingHorizontal: 12 },
  teamUpsellTxt: { flexShrink: 1, lineHeight: 16 },
  teamMemberRow: { flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch', maxWidth: 240, minHeight: 36, marginTop: 6 },
  teamMemberRowTxt: { color: '#8a8a95', fontSize: 11.5, fontWeight: '700', flexShrink: 1 },
  btnOfflineTxt: { fontSize: 12, fontWeight: '800', color: '#666', letterSpacing: 0.3 },

  stats: { flexDirection: 'row', backgroundColor: '#1a1a20', gap: 1 },
  stat: { flex: 1, backgroundColor: '#0d0d11', paddingVertical: 13, alignItems: 'center' },
  statV: { fontSize: 20, fontWeight: '900' },
  statL: { fontSize: 9.5, fontWeight: '800', letterSpacing: 0.6, color: '#6a6a74', marginTop: 5, textTransform: 'uppercase' },

  tabs: { flexDirection: 'row', gap: 16, paddingHorizontal: 18, paddingTop: 13, flexWrap: 'wrap' },
  tab: { color: '#8a8a95', fontSize: 13, fontWeight: '800', paddingVertical: 6, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  tabOn: { color: '#fff', borderBottomColor: '#FCD000' },
  body: { paddingHorizontal: 18, paddingTop: 4 },
  bodyEmpty: { color: '#7a7a85', fontSize: 13, textAlign: 'center', paddingVertical: 30 },

  gRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 11, borderBottomWidth: 1, borderBottomColor: '#15151a' },
  gHead: { borderBottomColor: '#1c1c22' },
  gName: { flex: 1, fontSize: 13, fontWeight: '700', color: '#ececf0' },
  gNum: { width: 64, textAlign: 'right', fontSize: 13, fontWeight: '700', color: '#aeb0b8' },
  crit: { color: '#FCD000', fontWeight: '800' },
  win: { color: '#4ade80' }, loss: { color: '#ff6b6b' },

  res: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 11, borderBottomWidth: 1, borderBottomColor: '#15151a' },
  place: { width: 34, height: 34, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: '#16161c' },
  p1: { backgroundColor: 'rgba(252,208,0,0.14)' }, p2: { backgroundColor: 'rgba(207,211,218,0.12)' }, p3: { backgroundColor: 'rgba(216,146,74,0.14)' },
  placeTxt: { fontSize: 15, fontWeight: '900', color: '#cfcfd6' },
  resName: { fontSize: 13, fontWeight: '700', color: '#ececf0' },
  resSub: { fontSize: 11, color: '#7a7a85', marginTop: 1 },
  resCrit: { fontSize: 13, fontWeight: '800', color: '#FCD000', textAlign: 'right' },
  resTime: { fontSize: 10, color: '#5a5a64', textAlign: 'right', marginTop: 1 },
  when: { width: 64, alignItems: 'center' },
  whenD: { fontSize: 11, fontWeight: '800', color: '#00bfff' },
  whenT: { fontSize: 13, fontWeight: '900', color: '#fff' },
  inPill: { fontSize: 11, fontWeight: '800', color: '#FCD000', backgroundColor: 'rgba(252,208,0,0.1)', borderWidth: 1, borderColor: 'rgba(252,208,0,0.35)', borderRadius: 7, paddingHorizontal: 9, paddingVertical: 5, overflow: 'hidden' },

  foot: { padding: 12, fontSize: 10, color: '#5a5a64', textAlign: 'center', borderTopWidth: 1, borderTopColor: '#1c1c22' },

  // Videos tab
  vManageBtn: { alignSelf: 'flex-end', marginBottom: 10 },
  vManageBtnTxt: { color: '#FCD000', fontSize: 12.5, fontWeight: '800' },
  vFilters: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 12 },
  vChip: {
    backgroundColor: '#1a1a1a', borderWidth: 1, borderColor: '#2e2e2e', color: '#aaa',
    fontSize: 11, fontWeight: '700', borderRadius: 999, paddingHorizontal: 10, paddingVertical: 4, overflow: 'hidden',
  },
  vChipOn: { backgroundColor: '#FCD00018', borderColor: '#FCD00055', color: '#FCD000' },
  vFilterDivider: { width: 1, height: 16, backgroundColor: '#2e2e2e', marginHorizontal: 4 },
  vGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  vCard: { width: '47%', minWidth: 150, flexGrow: 1, maxWidth: 260 },
  vThumb: {
    aspectRatio: 16 / 9, borderRadius: 10, backgroundColor: '#141414',
    borderWidth: 1, borderColor: '#242424', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden',
  },
  vThumbImg: { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' },
  vPlay: {
    width: 30, height: 30, borderRadius: 15, backgroundColor: '#000000aa',
    borderWidth: 1.5, borderColor: '#ffffff66', alignItems: 'center', justifyContent: 'center',
  },
  vPlayTxt: { color: '#fff', fontSize: 11 },
  vGameTag: {
    position: 'absolute', left: 6, top: 6, backgroundColor: '#000000cc', color: '#FCD000',
    fontSize: 9, fontWeight: '800', borderRadius: 4, paddingHorizontal: 5, paddingVertical: 1, overflow: 'hidden',
  },
  vDur: {
    position: 'absolute', right: 6, bottom: 6, backgroundColor: '#000000cc', color: '#fff',
    fontSize: 9.5, fontWeight: '700', borderRadius: 4, paddingHorizontal: 4, overflow: 'hidden',
  },
  vMembers: {
    position: 'absolute', right: 6, top: 6, flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#000000cc', borderRadius: 4, paddingHorizontal: 5, paddingVertical: 1,
  },
  vMembersLocked: { borderWidth: 1, borderColor: '#FCD00088' },
  vMembersTxt: { color: '#FCD000', fontSize: 9, fontWeight: '800' },
  vTitle: { color: '#fff', fontSize: 12.5, fontWeight: '700', lineHeight: 17, marginTop: 7 },
  vMeta: { color: '#7a7a85', fontSize: 11, marginTop: 3 },
  emptyTxt: { color: '#9a9aa5', fontSize: 14, fontWeight: '700' },

  // Edit modal
  mOverlay: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16 },
  mScrim: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.75)' },
  mCard: { width: '100%', maxWidth: 460, backgroundColor: '#0f0f12', borderRadius: 16, borderWidth: 1, borderColor: '#2a2a30', padding: 18 },
  mTitle: { fontSize: 17, fontWeight: '900', color: '#fff', marginBottom: 12 },
  mLabel: { fontSize: 11, fontWeight: '800', letterSpacing: 0.4, color: '#8a8a95', textTransform: 'uppercase', marginTop: 12, marginBottom: 6 },
  mInput: { backgroundColor: '#16161c', borderWidth: 1, borderColor: '#2a2a32', borderRadius: 9, color: '#fff', fontSize: 13, paddingHorizontal: 12, paddingVertical: 10, marginBottom: 4 },
  mArea: { minHeight: 72, textAlignVertical: 'top' },
  mHint: { fontSize: 10, color: '#6a6a74', marginBottom: 4 },
  mImgRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 },
  mImgLabel: { fontSize: 12.5, fontWeight: '700', color: '#ececf0' },
  mSocRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  mSocIcon: { width: 32, height: 32, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  mActions: { flexDirection: 'row', gap: 10, marginTop: 14, justifyContent: 'flex-end' },
});
